import { useEffect, useState, useMemo } from 'react';
import { useAppContext, SET_CHOSEN_PLANTS, SET_FILTERED_LIST } from '../context/AppContext';
import { useSpreadsheetContext } from '../context/SpreadsheetContext';
import { useGardenBed } from '../hooks/useGardenBed.js';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../utils/urlState';
import PlantList from '../components/PlantList.jsx';
import ChoosePlantSideWindow from '../components/ChoosePlantSideWindow.jsx';
import gardenBedLayouts from '../data/gardenBedLayouts.json';
import ChoosePlantPreviewSVG from '../components/ChoosePlantPreviewSVG.jsx';
import ChoosePlantsMobile from './ChoosePlants.mobile.jsx';
import nextButton from '../images/ui_buttons/next_button.png';
import backButton from '../images/ui_buttons/back_button.png';
import { Container } from '@mantine/core';
import { MobileView, BrowserView } from 'react-device-detect';
import PropTypes from 'prop-types';

const ChoosePlants = ({ gardenBedId }) => {
  const { state, dispatch } = useAppContext();
  const { plantData } = useSpreadsheetContext();
  const { zone, style, sunlight, chosenPlants = [], bedConfig } = state.currentDesign || {};
  const { user } = state;
  const { addOrReplacePlant, plantCategories } = useGardenBed(gardenBedId);
  const [filteredPlants, setFilteredPlants] = useState([]);
  const [currentPlantCategory, setCurrentPlantCategory] = useState(null);
  const [selectedPlantId, setSelectedPlantId] = useState(null);
  const navigate = useNavigate();
  const { navigateWithParams } = useUrlState();

  // Build the initial plants array to be saved to design, ignore if already built
  useEffect(() => {
    if (chosenPlants.length === 0 && bedConfig) {
      const currentLayout = gardenBedLayouts.find((layout) => layout.id === bedConfig);
      const initialPlantsArray = currentLayout.plants.map((plant) => ({
        category: plant.category,
        plantId: null,
        quantity: plant.quantity,
      }));

      dispatch({ type: SET_CHOSEN_PLANTS, payload: initialPlantsArray });
    }
  }, [chosenPlants, bedConfig, dispatch]);

  // Get all plants that fit Zone, Style, and Sunlight
  const initialFilteredPlants = useMemo(() => {
    if (!plantData || plantData.length === 0) return [];

    return plantData.filter((plant) => {
      const zones = plant.Zone.split(',').map((r) => r.trim());
      const styles = plant.Style.split(',').map((r) => r.trim());
      const sunlightConditions = plant.Sunlight.split(',').map((r) => r.trim());

      return (
        zones.includes(zone) &&
        styles.includes(style) &&
        sunlightConditions.includes(sunlight)
      );
    });
  }, [plantData, zone, style, sunlight]);

  // Save initialFilteredPlants to AppContext whenever it changes
  useEffect(() => {
    dispatch({ type: SET_FILTERED_LIST, payload: initialFilteredPlants });
  }, [initialFilteredPlants, dispatch]);

  // Filter the previously filtered plant list to only those with the same category as is currently showing
  useEffect(() => {
    let updatedPlants = initialFilteredPlants;

    if (currentPlantCategory) {
      updatedPlants = updatedPlants.filter(
        (plant) => plant.Category === currentPlantCategory
      );
    }

    setFilteredPlants(updatedPlants);
  }, [currentPlantCategory, initialFilteredPlants]);

  // Navigate to the next screen
  const handleNext = () => {
    navigateWithParams('/summary-one');
  };

  // Navigate to the previous screen
  const handleBack = () => {
    navigate('/garden-bed');
  };

  // Handle subscription button click
  const handleSubscribe = () => {
    window.open('https://www.mylandscapedesign.com/pricing-plans', '_blank');
  };

  // Logic for choosing selected plant
  const handleSelectPlant = (plantId, category) => {
    if (plantId === null) {
      dispatch({
        type: SET_CHOSEN_PLANTS,
        payload: chosenPlants.map((p) =>
          p.category === category ? { ...p, plantId: null } : p
        ),
      });
    } else {
      addOrReplacePlant(plantId, category);
    }
    setSelectedPlantId(plantId);
  };

  return (
    <>
      <MobileView>
        <ChoosePlantsMobile gardenBedId={gardenBedId} />
      </MobileView>
      <BrowserView>
        <div className='full-container'>
        <header>
          <button onClick={handleBack}>
            <img src={backButton} alt='Back' className='button-image' />
          </button>
          <h1>Choose your Plants</h1>
          {user.hasActiveSubscription ? (
            <button onClick={handleNext}>
              <img src={nextButton} alt='Next' className='button-image' />
            </button>
          ) : (
            <button 
              onClick={handleSubscribe}
              style={{
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                padding: '15px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                whiteSpace: 'normal',
                maxWidth: '300px',
                textAlign: 'center',
                minHeight: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              Subscribe to My Landscape Design to see plant information
            </button>
          )}
        </header>
        {/* <div className='choose-plant-body-container'> */}
        <div className='choose-plant-top-container'>
        <Container
            fluid 
            style={{
              display: 'flex',
              minHeight: '100%',
              height: '100%',
              width: '100%',
              overflow: 'auto',
              margin: '0 5px',
            }}
          >
            <ChoosePlantPreviewSVG />
          </Container>
          <ChoosePlantSideWindow
            selectedPlantId={selectedPlantId}
            selectedPlantData={plantData.find((plant) => plant.ID === selectedPlantId)}
            onCategoryChange={(category) => setCurrentPlantCategory(category)}
          />
        </div>
          <PlantList
            plants={filteredPlants}
            plantCategories={plantCategories}
            selectedPlantId={selectedPlantId}
            currentPlantCategory={currentPlantCategory}
            onSelect={(plantId, category, quantity) =>
              handleSelectPlant(plantId, category, quantity)
            }
          />
          {/* </div> */}
        </div>
      </BrowserView>
    </>
  );
};

ChoosePlants.propTypes = {
  gardenBedId: PropTypes.string.isRequired,
};

export default ChoosePlants;
